import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { cyan, border, card, amber, green, red, section, sectionTitle, btn, btnPrimary, todayKey } from "./fieldUi";
import { hasModule } from "../utils/orgType";

// Role home screens (docs/audit/WORKFLOW-AUDIT.md stage 6) — the next
// action first, for a rig-service / hydro company:
//   toolpusher     "Today's report for Rig 7 — continue" (or start it)
//   crew           today's report for their rig
//   hydro operator today's tickets + "+ New ticket"
//   office         Billing counts (Needs fixing · Ready to bill · Exported)
//   admin          the dashboard as it is (no card)
// "Their rig" is the crew roster (Users → rig + position); without one the
// card says so and offers the rig picker.
export function homeOf(user) {
  if (!user) return "admin";
  if (user.role === "admin") return "admin";
  const pos = user.crewPosition || "";
  if (pos === "office") return "office";
  if (user.role !== "field_hand") return user.role === "supervisor" && pos === "" ? "admin" : pos === "toolpusher" ? "toolpusher" : pos === "hydro_operator" ? "hydro" : pos === "crew" ? "crew" : "admin";
  if (pos === "toolpusher") return "toolpusher";
  if (pos === "hydro_operator") return "hydro";
  if (pos === "crew") return "crew";
  // not on the roster yet: hydro-only companies → hydro, else the rig crew
  return hasModule(user, "rigServices") ? "crew" : "hydro";
}

const API = () => process.env.REACT_APP_API_URL;
const bigBtn = { ...btnPrimary, minHeight: "64px", fontSize: "18px", width: "100%", textAlign: "left", padding: "12px 16px" };

export default function RoleHome({ user, token, onOpenReport, onStartReport, onPickRig, onOpenHydro, onNewHydro, onOpenBilling }) {
  const { t } = useTranslation();
  const kind = homeOf(user);
  const headers = { Authorization: "Bearer " + token };
  const [state, setState] = useState(null);

  useEffect(() => {
    let live = true;
    const today = todayKey();
    const done = (s) => live && setState(s);
    if (kind === "toolpusher" || kind === "crew") {
      axios
        .get(`${API()}/api/reports/service-dashboard`, { headers, params: { today, scope: "mine" }, timeout: 20000 })
        .then((r) => done({ today: r.data?.today || today, rig: (r.data?.rigs || []).find((x) => user.assignedRigId && String(x.rigId) === String(user.assignedRigId)) || null }))
        .catch(() => done({ today, rig: null, error: true }));
    } else if (kind === "hydro") {
      // hydro only: today's tickets, the ones to fix or still unsigned, own certs
      const soon = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
      Promise.all([
        axios.get(`${API()}/api/hydro-tickets`, { headers, timeout: 20000 }).then((r) => (Array.isArray(r.data) ? r.data : r.data?.data || [])),
        axios
          .get(`${API()}/api/personnel-certifications`, { headers, timeout: 20000 })
          .then((r) => (Array.isArray(r.data) ? r.data : r.data?.data || []))
          .catch(() => []),
      ])
        .then(([list, certs]) => {
          const todays = list.filter((x) => x.dateStarted === today);
          const fix = list.filter((x) => x.dateStarted !== today && (x.needsFixing || (x.status === "draft" && !x.signed)));
          done({ today, tickets: todays.slice(0, 8), fix: fix.slice(0, 8), certs: certs.filter((c) => c.expirationDate && String(c.expirationDate).slice(0, 10) <= soon).slice(0, 5) });
        })
        .catch(() => done({ today, tickets: [], fix: [], certs: [], error: true }));
    } else if (kind === "office") {
      axios
        .get(`${API()}/api/billing/queue`, { headers, timeout: 20000 })
        .then((r) => done({ counts: r.data?.counts || null }))
        .catch(() => done({ counts: null, error: true }));
    }
    return () => {
      live = false;
    };
    // eslint-disable-next-line
  }, [kind, token, user.assignedRigId]);

  if (kind === "admin") return null;
  const wrap = (children) => (
    <section style={{ ...section, borderColor: cyan }} data-testid={`home-${kind}`}>
      {children}
    </section>
  );
  if (!state) return wrap(<div style={{ color: "rgba(255,255,255,0.6)" }}>{t("home.loading")}</div>);

  if (kind === "toolpusher" || kind === "crew") {
    const rig = state.rig;
    const rigLabel = rig ? rig.rigNumber || rig.name : "";
    if (!user.assignedRigId || !rig)
      return wrap(
        <>
          <h3 style={sectionTitle}>{t("home.today")}</h3>
          <p style={{ color: "rgba(255,255,255,0.7)", margin: "0 0 10px" }}>{t("home.noRig")}</p>
          <button type="button" data-testid="home-pick-rig" style={{ ...btn, minHeight: "48px" }} onClick={onPickRig}>
            {t("home.pickRig")}
          </button>
        </>,
      );
    const report = rig.todayReport;
    return wrap(
      <>
        <h3 style={sectionTitle}>{t("home.today")}</h3>
        {report ? (
          <button type="button" data-testid="home-continue" style={bigBtn} onClick={() => onOpenReport(report._id)}>
            {kind === "toolpusher" ? t("home.continue", { rig: rigLabel }) : t("home.open", { rig: rigLabel })}
            <div style={{ fontSize: "13px", fontWeight: 400, opacity: 0.8 }}>{t(`rigReport.status.${report.status}`)}</div>
          </button>
        ) : (
          <button type="button" data-testid="home-start" style={bigBtn} onClick={() => onStartReport(rig.rigId, state.today)}>
            {t("home.start", { rig: rigLabel })}
          </button>
        )}
        {(rig.currentWell?.wellName || rig.job?.wellName) && <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", marginTop: "8px" }}>{rig.currentWell?.wellName || rig.job?.wellName}</div>}
      </>,
    );
  }

  const ticketRow = (x, testid, note) => (
    <button key={x._id} type="button" data-testid={testid} onClick={() => onOpenHydro(x._id)} style={{ display: "block", width: "100%", textAlign: "left", background: card, border: `1px solid ${border}`, borderRadius: "8px", padding: "10px 12px", marginBottom: "6px", color: "white", cursor: "pointer", minHeight: "48px" }}>
      <strong>{x.ticketNumber}</strong> · {x.customerName || "—"} <span style={{ color: amber, fontSize: "13px" }}>· {note || t(`hydro.status.${x.status}`)}</span>
    </button>
  );
  if (kind === "hydro")
    return wrap(
      <>
        <h3 style={sectionTitle}>{t("home.todayTickets")}</h3>
        <button type="button" data-testid="home-new-ticket" style={{ ...bigBtn, marginBottom: "10px" }} onClick={onNewHydro}>
          {t("home.newTicket")}
        </button>
        {state.tickets.length === 0 && <div style={{ color: "rgba(255,255,255,0.6)" }}>{t("home.noTickets")}</div>}
        {state.tickets.map((x) => ticketRow(x, "home-ticket"))}
        {(state.fix || []).length > 0 && (
          <>
            <h3 style={{ ...sectionTitle, marginTop: "14px" }}>{t("home.hydroFix")}</h3>
            {state.fix.map((x) => ticketRow(x, "home-ticket-fix", x.needsFixing ? t("stage.needs_fixing") : t("home.unsigned")))}
          </>
        )}
        {(state.certs || []).length > 0 && (
          <>
            <h3 style={{ ...sectionTitle, marginTop: "14px" }}>{t("home.myCerts")}</h3>
            {state.certs.map((c) => (
              <div key={c._id} data-testid="home-cert" style={{ fontSize: "14px", padding: "4px 0", color: String(c.expirationDate).slice(0, 10) < state.today ? red : amber }}>
                {t(`certTypes.${c.certificationType}`, { defaultValue: c.certificationType })} · {String(c.expirationDate).slice(0, 10)}
              </div>
            ))}
          </>
        )}
      </>,
    );

  // office: Billing counts
  const c = state.counts || { needsFixing: 0, readyToBill: 0, exported: 0 };
  const tile = (k, color) => (
    <button key={k} type="button" data-testid={`home-billing-${k}`} onClick={() => onOpenBilling(k)} style={{ background: card, border: `1px solid ${border}`, borderRadius: "10px", padding: "12px", color: "white", cursor: "pointer", textAlign: "left", minHeight: "64px" }}>
      <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)" }}>{t(`billing.tabs.${k}`)}</div>
      <div style={{ fontSize: "26px", fontWeight: 800, color }}>{c[k]}</div>
    </button>
  );
  return wrap(
    <>
      <h3 style={sectionTitle}>{t("home.billing")}</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
        {tile("needsFixing", c.needsFixing ? red : green)}
        {tile("readyToBill", cyan)}
        {tile("exported", "rgba(255,255,255,0.8)")}
      </div>
    </>,
  );
}
